// ---------- point de croix : une croix de fil par case, sur toile écrue ----------
//   0         toile nue : le fond qu'on ne brode pas, comme sur une vraie grille
//   1..256    une croix, dans une des couleurs du nuancier de `peintureNuancee` — mêmes codes,
//             mêmes noms ; une vingtaine par ouvrage (cf. l'encodeur)
//
// Né le 2026-10-22 de la planche « Points comptés » :
// https://claude.ai/artifact/RojFh7Q8PvTzS8uMRF67Qr — croix pleine,
// toile écrue partout, sans finitions. Les trois-quarts automatiques (un coin brodé du fil des deux
// voisines qui le partagent, pour adoucir les diagonales) ont été retirés le même jour : à l'essai,
// ils n'apportaient rien de visible.
//
// LA CROIX EN FUSEAU, ET C'EST TOUT LE DESSIN. Un trait de largeur constante obligeait à choisir :
// fin (0,42 case, l'essai du 11 octobre), la toile perce au milieu des bords de chaque case et
// dessine un quadrillage clair sur tout l'ouvrage ; épais, la case est couverte mais on ne
// reconnaît plus la croix. Une vraie jambe de croix est gonflée au milieu, là où les deux brins
// s'étalent, et pincée aux bouts, là où elle plonge dans les trous de la toile : le centre reste
// couvert, les quatre pointes dessinent le X.
//
// LA TOILE ENTRE LES POINTES EST LE PAPIER. Elle a d'abord été sombre, teintée par les fils, pour
// qu'elle se lise comme le creux de l'ouvrage et non comme une grille claire ; puis seulement
// entre deux croix, parce qu'au bord du sujet et au bord de ce qu'on avait déjà brodé elle cernait
// chaque croix d'une bande sombre. Retirée le même jour (2026-10-22, « pas d'intérêt ») : avec des
// jambes en fuseau, la toile visible n'est plus qu'un petit losange entre quatre pointes, comme sur
// une vraie broderie. La conversion en tient compte (cf. farColor). Ce losange a été RÉDUIT le même
// soir : à 0,5 de large et 0,28 aux pointes, il laissait 22 % de la case en papier, et dans les
// vidéos de remplissage « ça perce trop, on voit trop le tissu blanc ». À 0,58 et 0,36, le papier
// tombe à 10 % et le X se lit encore ; à 0,66 et 0,45 (3 %), on retombait sur des carrés.
//
// LE CREUX OMBRÉ, ENTRE DEUX CROIX (2026-10-23). Même à 10 %, le renard sortait « délavé, on voit
// trop le tissu » : ces losanges étaient la chose la plus claire de l'ouvrage, sur chaque bord de
// chaque case, et de loin ils voilaient tout de blanc. Sur une vraie broderie la toile entre les
// croix n'est jamais claire : elle est en creux, à l'ombre des fils en relief, et teintée par le
// coton translucide autour d'elle. Elle prend donc ici le fil de la case, multiplié par l'écru et
// assombri (HOLLOW) — un ton moyen, pas l'ombre de la toile sombre retirée plus haut. Et seulement
// du côté d'une voisine brodée : chaque case partage son fond en quatre triangles, un par bord, et
// garde le papier vers la toile nue, si bien que le sujet ne se cerne de rien. Planche de
// comparaison : https://claude.ai/artifact/FrJmLTEqRnRhZ2GgCvgvaE — l'ouvrage passait de 5 à 8 %
// plus clair que ses fils en aplat à 5 à 7 % plus sombre, comme une vraie broderie.
//
// LE REFLET RETENU, le même jour : un mouliné mercerisé brille sur un filet fin, et ce qu'on voit
// d'abord est le sillon entre ses deux brins. À +35 % vers le blanc, le reflet éclaircissait toute
// la jambe ; +12 % (et la brillance de +18 à +6 %).
//
// LES TROUS DE LA TOILE, SEULEMENT DANS L'OUVRAGE. Ils marquent les coins entre deux croix, pas le
// contour du sujet ni le bord de ce qui est déjà brodé, où ils pointillaient le papier. Lu sur la
// grille (prepareGrid) et sur les cases déjà peintes (prepareFilled) ; le jeu redessine les
// voisines de chaque case qu'il pose. Hors du jeu, sans état de partie, la grille est dessinée
// comme finie.

import { NUANCES, peintureNuancee } from './peintureNuancee';
import { hexRgb } from './peinture';
import type { Alphabet } from './types';

/** La largeur d'une jambe en son milieu, et à ses bouts, en fraction de case. */
export const LEG_W = 0.58;
const TIP = 0.36; // fraction de LEG_W
/** Le retrait des bouts de jambe depuis les coins : là où sont les trous. */
const INSET = 0.04;

const RGB = NUANCES.map(hexRgb);
/** Le papier du jeu, qui fait la toile (--paper-rgb dans src/css/style.css, le même pour tous les
 *  thèmes). */
const PAPER: readonly [number, number, number] = [251, 246, 236];
const css = (c: readonly number[]) => `rgb(${c[0]},${c[1]},${c[2]})`;
const shade = (c: readonly number[], k: number) =>
  k < 0 ? c.map((v) => Math.round(v * (1 + k))) : c.map((v) => Math.round(v + (255 - v) * k));
/** L'ombre portée d'une jambe. */
const SHADOW = -0.55;
/** Le creux entre deux croix : le fil multiplié par l'écru, puis assombri d'autant (cf. l'en-tête). */
const HOLLOW = -0.35;
const hollowOf = (c: readonly number[]) =>
  shade(
    c.map((v, i) => (v * PAPER[i]) / 255),
    HOLLOW,
  );
const LIGHT = 0.12;
const SHEEN = 0.06;
const PAPER_CSS = css(PAPER);

// Ce qu'une case rend de loin, en parts de sa surface : mesuré en rastérisant la croix (fuseaux,
// retrait et ombre de draw, 160 × 160 échantillons, 2026-10-22). À refaire si la forme change.
const FAR_THREAD = 0.856;
const FAR_SHADOW = 0.045;

/** Ce qu'une croix rend de loin : son fil, son ombre portée, et la toile entre les pointes. La
 *  couleur que l'encodeur compare à l'image.
 *
 *  Elle compte encore le PAPIER entre les pointes, alors que le dessin y met le creux (cf.
 *  l'en-tête) — et c'est voulu. Comptée avec le creux, la couleur de loin redevient presque celle
 *  du fil, l'encodeur choisit des fils plus clairs, et sur le renard du 2026-10-23 l'orange vif
 *  sortait des vingt fils : tout virait au pêche. Le papier compté pousse vers des fils plus
 *  profonds, que le creux rend ensuite à leur vraie couleur. */
export function farColor(c: readonly number[]): [number, number, number] {
  return mix(c, PAPER);
}

/** Ce qu'une croix rend de loin, la toile entre les pointes étant `ground`. */
function mix(c: readonly number[], ground: readonly number[]): [number, number, number] {
  const sh = shade(c, SHADOW);
  const p = 1 - FAR_THREAD - FAR_SHADOW;
  return [0, 1, 2].map((t) =>
    Math.round(FAR_THREAD * c[t] + FAR_SHADOW * sh[t] + p * ground[t]),
  ) as [number, number, number];
}

// LES SEUILS DE DÉTAIL, en pixels d'écran par case. Le quadrillage NOIR vu au dézoom de l'atelier
// (2026-10-22) : son aperçu dessinait toujours à 10 px la case, puis le navigateur réduisait
// l'image. Les trous des coins, posés au même endroit sur chaque case, tombaient alors en lignes
// régulières et se lisaient comme une grille tracée sur l'ouvrage. L'atelier dessine désormais à
// la taille affichée, plafonnée à 10 px ; les trous restent réservés aux cases d'au moins 12 px,
// celles du jeu zoomé, où on les voit vraiment comme des trous.
const HOLES_PX = 12;
/** En dessous, la croix n'est qu'une poignée de pixels : un aplat de sa couleur de loin, sans
 *  texture à replier en moirage, et bien moins de travail sur une grande grille dézoomée. */
const FLAT_PX = 4;
/** Les brins (sillon et reflets) : 6 traits par case, d'un demi-pixel sous 12 px, donc invisibles
 *  là où ils coûtaient le plus. Mesure du 2026-10-22 (Chrome, processeur ×4, une feuille de
 *  1081 × 1900 px) : la vue d'ensemble d'une grille de 150 prenait 680 ms, contre 220 pour la
 *  peinture nuancée et 200 pour les briques nuancées. Brins dès 12 px, teintes précalculées
 *  (inksOf) et jambes remplies deux par deux ramènent ce poste à leur niveau. */
const BRINS_PX = 12;

/**
 * Le contour d'une jambe en fuseau, de (x1, y1) à (x2, y2), large de `w` en son milieu — ajouté au
 * chemin en cours, sans `beginPath` : les deux jambes d'une croix se remplissent en un seul appel.
 * Leur chevauchement reste plein (règle nonzero) : les deux contours tournent dans le même sens,
 * quelle que soit la direction de la jambe.
 */
function fuseau(
  g: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  w: number,
): void {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const L = Math.hypot(dx, dy);
  const nx = -dy / L;
  const ny = dx / L;
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const h0 = (w * TIP) / 2;
  const k = w - h0; // le point de contrôle qui fait passer le bord à w/2 au milieu
  g.moveTo(x1 + nx * h0, y1 + ny * h0);
  g.quadraticCurveTo(mx + nx * k, my + ny * k, x2 + nx * h0, y2 + ny * h0);
  g.lineTo(x2 - nx * h0, y2 - ny * h0);
  g.quadraticCurveTo(mx - nx * k, my - ny * k, x1 - nx * h0, y1 - ny * h0);
  g.closePath();
}

/** Un triangle ajouté au chemin en cours : le quart du fond d'une case, d'un bord au centre. */
function tri(
  g: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
): void {
  g.moveTo(x1, y1);
  g.lineTo(x2, y2);
  g.lineTo(x3, y3);
  g.closePath();
}

/** Les teintes d'un fil, en chaînes CSS : calculées une fois par code et non à chaque case (cf.
 *  BRINS_PX pour la mesure qui l'a demandé). */
interface Inks {
  far: string;
  thread: string;
  shadow: string;
  groove: string;
  light: string;
  sheen: string;
  hollow: string;
}
const inks: (Inks | undefined)[] = [];
function inksOf(code: number): Inks {
  const c = RGB[code - 1];
  return (inks[code] ??= {
    // l'aplat des petites tailles : la croix telle qu'on la dessine, creux compris — celui d'une
    // case au milieu de l'ouvrage, comme presque toutes (cf. farColor pour ce que voit l'encodeur)
    far: css(mix(c, hollowOf(c))),
    thread: css(c),
    shadow: css(shade(c, SHADOW)),
    groove: css(shade(c, -0.3)),
    light: css(shade(c, LIGHT)),
    sheen: css(shade(c, SHEEN)),
    hollow: css(hollowOf(c)),
  });
}

/** Une jambe vue de près : son ombre portée, le fuseau, ses brins. */
function leg(
  g: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  w: number,
  k: Inks,
  ox: number,
  oy: number,
): void {
  g.beginPath();
  fuseau(g, x1 + ox, y1 + oy, x2 + ox, y2 + oy, w);
  g.fillStyle = k.shadow;
  g.fill();
  g.beginPath();
  fuseau(g, x1, y1, x2, y2, w);
  g.fillStyle = k.thread;
  g.fill();
  strands(g, x1, y1, x2, y2, w, k);
}

/** Le sillon entre les deux brins d'une jambe, et un reflet sur chacun. */
function strands(
  g: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  w: number,
  k: Inks,
): void {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const L = Math.hypot(dx, dy);
  const nx = -dy / L;
  const ny = dx / L;
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const line = (off: number, col: string, lw: number) => {
    g.strokeStyle = col;
    g.lineWidth = lw;
    g.beginPath();
    g.moveTo(x1 + dx * 0.12 + nx * off * 0.3, y1 + dy * 0.12 + ny * off * 0.3);
    g.quadraticCurveTo(
      mx + nx * off * 1.3,
      my + ny * off * 1.3,
      x2 - dx * 0.12 + nx * off * 0.3,
      y2 - dy * 0.12 + ny * off * 0.3,
    );
    g.stroke();
  };
  g.lineCap = 'round';
  line(0, k.groove, w * 0.07);
  line(-w * 0.2, k.light, w * 0.09);
  line(w * 0.2, k.sheen, w * 0.06);
}

// La grille courante (cf. l'en-tête : l'ombre s'arrête où l'ouvrage s'arrête).
let grid: readonly (readonly number[])[] | null = null;
let gx0 = 0;
let gy0 = 0;
let filled: readonly (readonly boolean[])[] | null = null;

/** Une case brodée, ou à broder hors du jeu. Une fonction du module et non une fermeture dans draw :
 *  celle-ci était recréée à chaque case, et la lecture des voisines se fait désormais à toutes les
 *  tailles. */
function stitched(r: number, c: number): boolean {
  return !!grid![r]?.[c] && (!filled || filled[r]?.[c] !== false);
}

export const pointDeCroix: Alphabet = {
  id: 'pointDeCroix',
  label: 'Point de croix',
  symbolName: 'fil',
  selfColored: true,
  helpText:
    'Une croix de fil par case, sur une toile écrue qu’on ne brode pas au fond : une vingtaine de ' +
    'fils choisis pour chaque ouvrage parmi deux cent cinquante-six.',

  aspect: () => 1,
  // UNE image, pas le damier (2026-10-23). Le damier rend les joints que deux voisines lissent
  // chacune de leur côté ; ici la toile de chaque case est un fillRect plein, aligné sur les
  // pixels de l'image, et il n'y a pas de joint à rendre. Le damier coûtait cher : deux images dans
  // le même budget, donc 12 px par case au lieu de 16, et l'image cessait de servir passé 36 px
  // par case à l'écran — au retour de la récolte, le zoom finissait en croix dessinées une à une,
  // 134 ms par image au processeur ×4 sur le renard en 82 × 110. À 16 px elle tient jusqu'à 48.
  // Et elle est PLUS fidèle : image en mouvement contre dessin exact au même cadrage, 3,2 / 4,4 /
  // 5,0 sur 255 d'écart moyen à 14, 21 et 28 px par case, contre 4,2 / 6,2 / 6,9 en damier ; à
  // égalité dans la vue d'ensemble (3,1 contre 2,8).
  sheetMode: 'single',
  // Le dessin d'une case reste dans sa case ; ses trous dépendent des voisines peintes, et le jeu
  // redessine celles-ci avec elle (cf. prepareFilled) : une case posée par-dessus la couche rend ce
  // qu'une reconstruction rendrait.
  bakesOnTop: true,

  paletteOrder: peintureNuancee.paletteOrder,
  // Pas de numéros sur les cases à broder : un code est le rang d'une couleur dans le nuancier de
  // 256, donc souvent un nombre à trois chiffres, qui encombrait la case (vu dans le jeu le
  // 2026-10-22). La surbrillance du fil en cours guide le joueur, comme en peinture nuancée.
  bareSheet: true,
  // … mais la grille reste : sans la toile sombre entre les croix, rien d'autre ne montrait où
  // sont les cases à broder (demandé le 2026-10-22). Elle s'efface avec la feuille finie.
  ruledSheet: true,
  codeName: (code) => peintureNuancee.codeName?.(code) ?? '',

  prepareGrid(target, originX = 0, originY = 0) {
    if (target !== grid) filled = null; // l'état d'une autre partie ne vaut rien ici
    grid = target;
    gx0 = originX;
    gy0 = originY;
  },

  prepareFilled(f) {
    filled = f;
  },

  draw(ctx, x, y, w, h, code) {
    if (code < 1 || code > RGB.length) return;
    const s = Math.min(w, h);
    const t = ctx.getTransform();
    const px = s * Math.hypot(t.a, t.b);
    const k = inksOf(code);
    if (px < FLAT_PX) {
      ctx.fillStyle = k.far;
      ctx.fillRect(x, y, w, h);
      return;
    }
    // Les bords qui touchent la toile nue : haut, droite, bas, gauche. Ils décident du creux et des
    // trous (cf. l'en-tête).
    //
    // UNE CASE QUI N'EST PAS CELLE DE LA GRILLE COURANTE est une croix seule, sur la toile nue :
    // ni creux ni trous. La grille est un état du module, gardé d'un appel à l'autre, et tout le
    // monde ne dessine pas dedans — l'éclat d'une case réussie (drawBursts dans render.ts, centré
    // sur une demi-case), l'icône d'un fil dans l'atelier (en 0, 0). Ils prenaient le voisinage
    // d'une case de la dernière partie ; tant que le fond était toujours le papier, ça ne touchait
    // que les trous des grandes tailles, mais le creux en dépend à toutes (revue du 2026-10-23).
    // Reconnue à deux signes : la position tombe sur une case, et la grille y porte ce fil. Sans
    // grille du tout, même chose.
    let bare = 15;
    if (grid) {
      const fr = (y - gy0) / h;
      const fc = (x - gx0) / w;
      const r = Math.round(fr);
      const c = Math.round(fc);
      bare =
        Math.abs(fr - r) > 0.01 || Math.abs(fc - c) > 0.01 || grid[r]?.[c] !== code
          ? 15
          : (stitched(r - 1, c) ? 0 : 1) |
            (stitched(r, c + 1) ? 0 : 2) |
            (stitched(r + 1, c) ? 0 : 4) |
            (stitched(r, c - 1) ? 0 : 8);
    }
    // La toile entre les pointes. Au milieu de l'ouvrage, presque toutes les cases : un aplat de
    // creux À LA PLACE du papier, donc rien de plus à dessiner qu'avant ; seules les cases du bord
    // paient un remplissage de plus, leurs triangles. Mesuré le 2026-10-23 (Chrome, processeur ×4,
    // le renard en 82 × 110, avant et après en alternance) : 14 à 16 µs par case de 6 et 9 px, 44 à
    // 48 µs à 14 et 24 px, des deux côtés — l'écart tient dans le bruit d'une série à l'autre.
    if (!bare) {
      ctx.fillStyle = k.hollow;
      ctx.fillRect(x, y, w, h);
    } else {
      ctx.fillStyle = PAPER_CSS;
      ctx.fillRect(x, y, w, h);
      if (bare !== 15) {
        const cx = x + w / 2;
        const cy = y + h / 2;
        ctx.beginPath();
        if (!(bare & 1)) tri(ctx, x, y, x + w, y, cx, cy);
        if (!(bare & 2)) tri(ctx, x + w, y, x + w, y + h, cx, cy);
        if (!(bare & 4)) tri(ctx, x + w, y + h, x, y + h, cx, cy);
        if (!(bare & 8)) tri(ctx, x, y + h, x, y, cx, cy);
        ctx.fillStyle = k.hollow;
        ctx.fill();
      }
    }
    if (px >= HOLES_PX) {
      // les trous de la toile aux coins, là où l'ouvrage continue. Chaque case n'en dessine que son
      // quart — un trou entier débordait sur les voisines, et l'ordre de dessin se voyait entre une
      // case posée par-dessus la couche et une reconstruction.
      ctx.fillStyle = 'rgba(0,0,0,0.45)';
      const q = s * 0.065;
      if (!(bare & 9)) ctx.fillRect(x, y, q, q);
      if (!(bare & 3)) ctx.fillRect(x + w - q, y, q, q);
      if (!(bare & 12)) ctx.fillRect(x, y + h - q, q, q);
      if (!(bare & 6)) ctx.fillRect(x + w - q, y + h - q, q, q);
    }
    const a = s * INSET;
    const lw = s * LEG_W;
    const x0 = x + a;
    const y0 = y + a;
    const x1 = x + s - a;
    const y1 = y + s - a;
    const ox = s * 0.035;
    const oy = s * 0.06;
    if (px >= BRINS_PX) {
      // assez grand pour voir quelle jambe passe dessus : « / » puis « \ », chacune avec son ombre
      // et ses brins, toujours dans ce sens comme le veut la règle — l'ombre de « \ » tombe sur « / »
      // Découpées à la case : l'ombre portée dépasse d'un vingtième au bout des jambes, et sans la
      // toile sombre pour la cacher l'ordre de dessin de deux voisines se voyait (une case posée
      // par-dessus la couche contre une reconstruction, 2026-10-22). Sous BRINS_PX le débord fait
      // moins d'un pixel, et la découpe coûterait sur des milliers de cases.
      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, w, h);
      ctx.clip();
      leg(ctx, x0, y1, x1, y0, lw, k, ox, oy);
      leg(ctx, x0, y0, x1, y1, lw, k, ox, oy);
      ctx.restore();
      return;
    }
    // en dessous, l'ordre ne se voit plus : les deux ombres en un remplissage, les deux jambes en un
    // autre (cf. BRINS_PX)
    if (px >= 4) {
      ctx.beginPath();
      fuseau(ctx, x0 + ox, y1 + oy, x1 + ox, y0 + oy, lw);
      fuseau(ctx, x0 + ox, y0 + oy, x1 + ox, y1 + oy, lw);
      ctx.fillStyle = k.shadow;
      ctx.fill();
    }
    ctx.beginPath();
    fuseau(ctx, x0, y1, x1, y0, lw);
    fuseau(ctx, x0, y0, x1, y1, lw);
    ctx.fillStyle = k.thread;
    ctx.fill();
  },

  swatch(ctx, w, h, code) {
    const s = Math.min(w, h) - 6;
    const prev = grid;
    // une pastille est une croix seule, sur la toile nue : sans grille, draw() ne cherche aucun
    // voisin (bare = 15), donc ni creux ni trous autour d'elle
    grid = null;
    pointDeCroix.draw(ctx, (w - s) / 2, (h - s) / 2, s, s, code);
    grid = prev;
  },
};
